/** Regex patterns to detect sensitive keys (field names).
 *
 * THIS IS THE CREDENTIAL VOCABULARY, and the split from `SENSITIVE_FIELDS` below is deliberate rather than historical.
 * The runtime key screens consult BOTH (`isSecretKey`, `const/telemetry-redaction.ts`), so the two arrays are
 * interchangeable there; but `SEC-FLOW-TELEMETRY-SECRET-KEY-EMISSION-001` derives its authoring-time `input-schema` arm
 * from THIS array ALONE, because that arm's cure is "take the credential off the flow input" and the regulated-identifier
 * half of `SENSITIVE_FIELDS` (`ssn`, `credit_card`, `bank_account`, the client-IP family) is legitimately carried ON flow
 * input -- the sessions write path documented on that set puts a client IP there on purpose. Firing the rule on those
 * would be a false positive whose only "cure" (renaming to a non-secret shape) would publish a live user's address.
 *
 * THE SECRET-KEY / PRIVATE-KEY MEMBERS ARE A BACK-PAYMENT (2026-08-09) and are the credential half of that same split.
 * Key names below are spelled as the string VALUES they are; a bare backticked token is a symbol pointer.
 * `'secret_key'`, `'private_key'` and `'privateKey'` were members of `SENSITIVE_FIELDS` -- so the RUNTIME withheld them
 * -- while matching nothing here, so the rule could not see a flow declaring one: `/secret(?:Key)?$/i` anchors on
 * `'secret'` or `'secretKey'` at end-of-name and a `'secret_key'` ends in `'_key'`, and no member mentioned
 * `'private'` at all. Measured over all 919 flow leaves at the time: ZERO new violations, so this closes a recognition
 * hole rather than opening a queue. The prefixed forms these add (`'app_private_key'`, `'stripe_secret_key'`,
 * `'signingPrivateKey'`) were withheld by NEITHER array before.
 *
 * TOKEN-HASH IS A MEMBER (2026-08-10), and the reason it was missing is the anchor: `/token$/i` is END-anchored, so a
 * name ending in `hash` slipped past it, and `token_hash` was absent from `SENSITIVE_FIELDS` too -- the SHA-256 of a
 * presented bearer therefore published verbatim into `wf_node_results.input_json` at every door leaf that carries it
 * (`verifyBearerRequestToken`'s step output, and the `token_hash` inputSchema of
 * `flows/ai-telemetry-enrollment-auth-lookup.ts`). The hash is NOT replayable, which is why it read as the safe half of
 * the pair; it is still a STABLE CORRELATABLE identifier for ONE credential, so a reader of the published corpus can
 * group every run that authenticated with a given token and follow it across enrollments. Non-replayable is not
 * non-identifying, and this array screens identifiers of credentials as well as credentials.
 *
 * `/^creds$/i` IS ANTICIPATORY, added the same day. The desk leaves (`flows/desk-ticket-executor.ts`,
 * `flows/desk-ticket-analyzer.ts`) merge a `creds` bundle INSIDE their wired handlers under a stated non-logged-channel
 * invariant, so nothing publishes it today -- but the KEY matched no member, so a future `with:` wiring would have
 * published the whole bundle. START-anchored on purpose: it names a bundle, and suffixed spellings such as `tokenCreds`
 * (`flows/auth-login.ts`, a verified-token PII trio) are a REGULATED-IDENTIFIER question this credential array does not
 * own.
 *
 * RECORDED RESIDUAL, still OPEN and deliberately NOT closed here: financial-account key shapes (`account_number`,
 * `routing_number`, plus the live prefixed forms `aba_*` / `swift_*`) match no member here and are not in
 * `SENSITIVE_FIELDS` either, so they publish under the publish-by-default posture. The obvious cure -- an
 * `/account[-_]?number$/i` pair in THIS array -- was measured and REFUSED, because recognition here is exactly what
 * `SEC-FLOW-TELEMETRY-SECRET-KEY-EMISSION-001`'s `input-schema` arm derives from: `flows/ledger-banc-create.ts`
 * declares a REQUIRED `account_number`, and would fire an arm whose BOTH remedies are wrong -- a bank-account write
 * flow cannot take the account number off its own input, and renaming it to a non-secret shape publishes a live account
 * number. The `aba_*` / `swift_*` half no longer has a leaf-level schema witness (the single-consumer
 * `ledger-banc-account-bank-instructions-update` leaf was folded into `flows/banc-account-bank-instructions.ts` on
 * 2026-08-18 and its six instruction properties became positional binds on that leaf's `write` node), which moves NO
 * telemetry: the same six values still ride that node's published `input_json`, they are simply no longer declared as a
 * child flow's `inputSchema` properties. The residual is unchanged; only one of its two schema witnesses is. That is the `client_ip` false positive this split exists to prevent, one array over. A literal set in
 * `SENSITIVE_FIELDS` dodges the rule but reaches only the bare spellings, leaving every live prefixed form publishing,
 * so it reads as closed while it is not. The shape that closes it is a THIRD member array -- the REGEX form of the
 * regulated-identifier half, consumed by `isSecretKey` (`const/telemetry-redaction.ts`) and NOT by the compliance
 * rule -- which is a cross-consumer change owned outside this leaf. Never a length or key-position heuristic.
 */

import { isValidString } from '@federa/const/string-guards';
import { EMAIL_EXTRACTION_REGEX } from '@federa/const/validation-patterns';
import { EXTRACT_STRING_INPUT } from '@federa/const/extract-string-input-values';
import { REDACTION_PLACEHOLDERS } from '@federa/const/redaction';
import { REQUEST_METADATA_KEY } from '@federa/const/request-metadata-sanitize';

export const SENSITIVE_KEY_PATTERNS = [
	/api[-_]?key$/i,
	/access[-_]?key$/i,
	/client[-_]?secret$/i,
	/secret(?:Key)?$/i,
	/secret[-_]key$/i,
	/private[-_]?key$/i,
	/token$/i,
	// NOT reachable by `/token$/i` above -- that member is END-anchored and this name ends in `hash`. See the header.
	/token[-_]?hash$/i,
	/access[-_]?token/i,
	/refresh[-_]?token/i,
	/^bearer$/i,
	/password/i,
	/credential/i,
	// Whole-name only: `credential` above is a substring member and does not reach the abbreviation.
	/^creds$/i,
	/authorization/i,
	// The composed `Authorization` HEADER VALUE under its own name. `/authorization/i` above is a substring member but
	// this name does not contain the word, and `/token$/i` is END-anchored on `token` -- so `authHeader` matched NOTHING
	// in this array while carrying exactly what an `authorization` key would. ANCHORED ON `header` and never on a bare
	// `auth`: `auth` is a substring of `author`, `authLevel`, `authMode` and `authTag`, all live non-secret key shapes in
	// this tree, so a bare-`auth` member is the over-redaction that makes a screen untrustworthy.
	//
	// MEASURED AT ZERO NEW VIOLATIONS before landing, because `SEC-FLOW-TELEMETRY-SECRET-KEY-EMISSION-001`'s
	// authoring-time `input-schema` arm derives from THIS array alone: no flow leaf declares an `auth*header` property
	// (the only in-tree spellings are a local helper name in `flows/handle-agent-completion.ts` and two request-header
	// locals in `tools/telephony/`). So this closes a recognition hole rather than opening a queue -- the same register
	// as the 2026-08-09 secret-key/private-key back-payment above. It is PREVENTIVE: no live emitter puts this name on a
	// published payload today.
	/auth[-_]?headers?$/i,
] as const;

/** Regex patterns to detect sensitive values (actual secrets in string content).
 *
 * SELF-IDENTIFYING tokens only: the WHOLE match is the secret, so a consumer may replace the entire match without
 * losing surrounding context. Context-delimited credentials (`?access_token=<v>`, `--password <v>`) do NOT belong
 * here -- their match necessarily includes a non-secret prefix worth keeping, which is what
 * `CREDENTIAL_CONTEXT_PATTERNS` expresses instead.
 *
 * Members carry NO `g` flag, so `.test` / `.exec` stay stateless for the `.some(...)` consumers
 * (`utils/security/redact-string.ts`, `tools/observability/secure-logger.ts`; `utils/deps/registry.ts` reaches this
 * array through `redactTelemetryPayload` rather than importing it); `scrubCredentialSpans` adds `g` per call rather
 * than storing it.
 *
 * ABSORBED THE TELEMETRY FORK (2026-08-07). The door-local secret vocabulary that left `const/telemetry.ts` (its
 * retirement register there names the old spelling) was a second vocabulary compiled only by
 * `utils/observability/ai-telemetry-secret-scrub.ts`; that scrubber now calls `scrubCredentialSpans`, so there is one
 * list again. Eight of its twelve arms were already covered here at equal or
 * WIDER reach (its `ghp_[A-Za-z0-9]{36}` against this file's `gh[posur]_[A-Za-z0-9]{20,}`, its case-sensitive
 * `Bearer\s+` against the `i`-flagged member, and so on); two more -- the `sk-ant-` and PEM members below -- were
 * added in the same change so the merge is a strict SUPERSET on recognized shapes, and the last two are the
 * high-entropy arms the next paragraph rejects.
 *
 * ITS TWO HIGH-ENTROPY ARMS WERE DELIBERATELY NOT ABSORBED -- `[A-Za-z0-9+/]{40,}` and its base64url twin
 * `[A-Za-z0-9_-]{40,}`. This SSOT rejects entropy arms on measured grounds recorded at the Twilio and JWT members
 * below: it is consulted against opaque high-entropy IDs, where length cannot separate a secret from a primary key, and
 * the base64url twin is worse still on the telemetry corpus it came from, because a 40-character run of `[A-Za-z0-9_-]`
 * is also an ordinary kebab-case repo path segment. Stated plainly rather than buried: this NARROWS the telemetry
 * scrubber on unrecognized-shape secrets, and the accepted cure is adding the shape here -- never a length heuristic.
 */
export const SENSITIVE_VALUE_PATTERNS = [
	/sk_(?:live|test)_[0-9a-z]+/i,
	/pk_(?:live|test)_[0-9a-z]+/i,
	/Bearer\s+[A-Za-z0-9\-_.]+/i,
	/AIza[0-9A-Za-z_-]{20,}/,
	/ya29\.[0-9A-Za-z\-_]+/,
	/xox[pbTras]-[0-9A-Za-z-]+/,
	/sk-[A-Za-z0-9]{20,}/,
	/1000\.[0-9a-f]{32}\.[0-9a-f]{32}/,
	// Twilio SIDs are EXACTLY 34 chars of hex behind a 2-letter prefix, and the `\b` anchors plus the hex class are
	// load-bearing: unanchored `AC[a-z0-9]{32}` matched any 34-char alnum run beginning `ac` INSIDE a longer id, which
	// fired on 8/400 real git SHAs and 39/400 64-hex Durable-Object ids in this tree. Whole-value consumers turned that
	// into `[REDACTED]` and the span scrubber spliced a sentinel through the middle of a legitimate identifier.
	/\bAC[0-9a-f]{32}\b/i,
	/\bSK[0-9a-f]{32}\b/i,
	/AIza[0-9A-Za-z\-_]{35}/,
	// Compact JWT / signed bearer claim. The header is base64url of `{"...`, hence always `eyJ`, and the two further
	// dot-separated segments are what distinguish it from an ordinary dotted identifier -- prefix-anchored rather than
	// entropy-based, because this SSOT is also consulted against opaque high-entropy IDs where an entropy arm cannot
	// tell a secret from a primary key. A non-compact header (`{ "alg"`) is deliberately not matched.
	/\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]*/,
	// AWS access-key id and GitHub tokens. Self-identifying prefixes with no benign reading, so the whole-value
	// consumers above correctly redact a log line carrying one; added with the span scrubber below, which needs the
	// same shapes and must not re-derive them.
	/AKIA[0-9A-Z]{16}/,
	/gh[posur]_[A-Za-z0-9]{20,}/,
	/github_pat_[A-Za-z0-9_]{20,}/,
	// Anthropic API key, and the reason it needs its own member rather than riding the `sk-` arm above: that arm is
	// `sk-[A-Za-z0-9]{20,}`, whose run BREAKS at the hyphen after `ant`, leaving three characters where twenty are
	// required. So every Anthropic key reaching a scrubbed payload published verbatim until this member landed -- the
	// exact silent under-cover `SEC-CREDENTIAL-VALUE-SCRUB-BYPASS-001` exists to catch.
	/sk-ant-[A-Za-z0-9_-]{16,}/,
	// PEM private-key BLOCK. Self-identifying at BOTH ends and the whole match is the secret, which is what makes it a
	// member here rather than a `CREDENTIAL_CONTEXT_PATTERNS` entry. The body repetition is bounded AND lazy on purpose:
	// an unterminated BEGIN header must not scan to end-of-string from every start position, and 8192 characters clears
	// an RSA-4096 block with headroom.
	/-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]{0,8192}?-----END [A-Z ]*PRIVATE KEY-----/,
] as const;

/** Sentinel replacing one credential SPAN inside an otherwise-diagnostic string.
 *
 * Deliberately distinct from `REDACTED_PLACEHOLDER` (`const/redaction.ts`), which replaces a WHOLE value: this token
 * tells a reader the surrounding text is intact and only the secret was cut, which is the property that keeps a
 * scrubbed failure description usable for the RCA it exists to drive.
 */
export const CREDENTIAL_SPAN_SENTINEL = '<credential-redacted>';

/**
 * URL userinfo `scheme://user:pass@host`: the username stays, the password goes; group 2 restores the `@`.
 *
 * The leading `(?<![a-z0-9+.-])[0-9+.-]*` is a ReDoS cure, and both halves are load-bearing. Without the lookbehind the
 * scheme's `[a-z][a-z0-9+.-]*` consumes to end-of-string and backtracks looking for `://` at EVERY start position --
 * measured O(n^2) on this tree (2026-08-06: 402.8ms at 32k chars, 4.06x per doubling, 23.7s on one 256,001-char value).
 * The lookbehind alone would be WRONG: it drops a scheme run beginning with a non-letter (`1http://user:pass@host`
 * matched at `h` before, and a bare lookbehind blocks every position in that run), silently publishing the password. The
 * `[0-9+.-]*` arm restores exactly those matches by letting the match START at the run boundary and skip forward to the
 * first letter.
 *
 * OUTPUT IS UNCHANGED, which is the property that matters rather than match-span equality: `scrubCredentialSpans` is the
 * only consumer and rebuilds its output as group1 + sentinel + group2, so the extra leading characters are re-emitted
 * verbatim. They sit INSIDE group 1 deliberately -- anything matched but outside a group is DELETED by that rebuild.
 */
const URL_USERINFO_PATTERN = /(?<![a-z0-9+.-])([0-9+.-]*[a-z][a-z0-9+.-]*:\/\/[^/\s:@]+:)[^/\s:@]+(@)/i;

/**
 * Shell/env assignment to a credential-shaped variable. The 8-char value floor keeps short sentinels readable while
 * still covering every real key.
 *
 * Carries a ReDoS cure of the same SHAPE as the sibling above -- start only at a run boundary, then skip forward inside
 * group 1 -- but the skip class is DIFFERENT and the difference is load-bearing. The URL pattern only has to reach the
 * first LETTER, because its `[a-z0-9+.-]*` then absorbs the rest of the run. Here a literal keyword must ALIGN, and a
 * keyword can begin part-way through a run of letters (`NOTASECRET=` matches on the `SECRET` at index 4), so the skip
 * must range over the whole `[A-Z0-9_]` class. It is LAZY so the engine tries the shortest skip first, which reproduces
 * the original's leftmost-start search order exactly; a greedy skip would find the last keyword instead of the first.
 *
 * A narrower `[0-9_]*` skip was tried first and is WRONG -- it silently stopped scrubbing `NOTASECRET=...`, caught by
 * differential fuzz against the pre-change pattern rather than by any unit arm.
 */
const ENV_ASSIGNMENT_PATTERN =
	/(?<![A-Z0-9_])([A-Z0-9_]*?(?:SECRET|TOKEN|PASSWORD|PASSWD|APIKEY|API_KEY|ACCESS_KEY|PRIVATE_KEY|CREDENTIALS?)(?:_[A-Z0-9_]+)?\s*=\s*)["']?[^\s"';&|]{8,}/;

/*
 * MEASURED REFUSAL (2026-08-07): the BARE LOWERCASE ASSIGNMENT form stays uncovered, deliberately.
 *
 * `api_key=<opaque>` with no `?`/`&`, no `--` and no uppercase matches none of the five arms below: the query-param arm
 * demands the `[?&]` anchor, the CLI arm the `--`, and `ENV_ASSIGNMENT_PATTERN` is deliberately case-SENSITIVE. Two
 * widenings were built and measured over the whole tree (11,631 files / 1,052,606 lines), counting only NEW fires:
 * making the assignment arm case-insensitive fired 155 times, and dropping the query arm's anchor fired 11 times. In
 * both samples the new fires were ORDINARY CODE, not credentials -- `const token = extractBearerToken(request)`, JSX
 * `getAuthToken={getAuthToken}`, `auth={auth}`, `const radiusToken = props.radius === 'sm' ? ...`. That is structural
 * rather than a corpus accident: every language here spells a declaration `<identifier> = <8+ non-space chars>`, and
 * this scrubber is handed exactly such text (tool inputs carry file contents and commands, error messages carry
 * frames). An over-scrub that eats the diagnostic is the failure the boundary lookarounds above already exist to avoid.
 *
 * WHY THE REFUSAL IS SAFE rather than a hole, measured the same day: every recognized VALUE shape is already scrubbed
 * in this exact form by `SENSITIVE_VALUE_PATTERNS`, independent of key case or assignment form -- `api_key=sk_live_...`,
 * `=sk-ant-...`, `=ghp_...`, `access_key=AKIA...` and `id_token=eyJ...` all redact today. The residual is therefore only
 * a bare lowercase key whose value has NO recognized shape, and the cure is this SSOT's standing one: add the SHAPE to
 * `SENSITIVE_VALUE_PATTERNS`, never a key-position or length heuristic. Both halves are pinned as MUST-NOT-FIRE /
 * MUST-FIRE fixtures in the sibling test rather than left as prose.
 */
/** Context-delimited credential patterns: the secret is a SPAN inside a larger string whose remainder is diagnostic.
 *
 * CONTRACT, relied on by `scrubCredentialSpans`: capture group 1 is the preserved PREFIX, and group 2 -- where a
 * pattern declares one -- is the preserved SUFFIX. Everything else the match consumes is the secret and is replaced.
 *
 * Each credential word sits behind boundary lookarounds so it must be a whole `-`/`_`/`.`-delimited segment of the
 * parameter or flag name. Without them `?monkey=`, `?sortkey=` and `?design=` each match on an interior substring and
 * scrub a benign value -- the over-scrub that makes a redactor untrustworthy and gets it switched off.
 *
 * No `g` flag, matching the sibling arrays above, so these stay stateless under `.test` / `.exec`.
 */
export const CREDENTIAL_CONTEXT_PATTERNS = [
	// Query parameter whose NAME is credential-shaped: `?access_token=`, `&api-key=`, presigned `&X-Amz-Signature=`.
	/([?&][^=&\s]*(?<![A-Za-z0-9])(?:access_?token|refresh_?token|id_?token|token|api_?key|apikey|access_?key|secret_?key|secret|password|passwd|pwd|signature|sig|credential|auth)(?![A-Za-z0-9])[^=&\s]*=)[^&\s"'<>]+/i,
	// Authorization-style header text. The scheme rides the preserved prefix so `Bearer` vs `Basic` survives triage.
	//
	// `auth[-_]?headers?` joined the header-NAME alternation 2026-08-15, and it is the free-text half of the same
	// recognition gap the `/auth[-_]?headers?$/i` key member closes: `tools/telephony/voice-number-config.ts` and its
	// purchase sibling compose a `Basic <base64>` credential into a local named `authHeader`, and while every live
	// reference is consumed as a request header today, a payload or a message quoting `authHeader: "Basic dXNlcjpwYXNz"`
	// reached NO arm here. It rides THIS array rather than `SENSITIVE_VALUE_PATTERNS` because a bare `Basic\s+<run>`
	// value member is a MEASURED false positive: unlike `Bearer`, `Basic` is an ordinary English adjective, so that arm
	// eats the value in `Basic authentication failed` and `Basic configuration error` -- the over-scrub the boundary
	// lookarounds on this array exist to avoid. A length floor does not separate them either (`Basic interoperability`
	// clears 16 characters), and this SSOT rejects length heuristics on the measured grounds recorded above.
	/((?:authorization|proxy-authorization|x-api-key|x-auth-token|x-amz-security-token|auth[-_]?headers?)["']?\s*[:=]\s*["']?(?:bearer|basic|digest|token)?\s*)[^\s"',;&]+/i,
	// Inline CLI credential flag, in either `--flag=value` or `--flag value` form.
	/(--(?:password|passwd|pwd|secret|token|api-?key|access-?key|secret-?key|auth-?token|credential)s?(?:=|\s+))[^\s]+/i,
	URL_USERINFO_PATTERN,
	ENV_ASSIGNMENT_PATTERN,
] as const;

/** `pattern` with the `g` flag, rebuilt per call so the stored SSOT members never carry `lastIndex` state. */
function withGlobalFlag(pattern: RegExp): RegExp {
	return pattern.flags.includes('g') ? pattern : new RegExp(pattern.source, `${pattern.flags}g`);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): span-preserving credential scrubber homed in const/ so pure utils/
 * leaves can value-import it without a forbidden cross-utils sibling dependency --
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` has no allowlist, and both first consumers
 * (`utils/hooks/post-tool-use-failure-payload-fields.ts`, `utils/hooks/post-tool-use-failure-structured-output.ts`) are
 * themselves utils/ leaves. Same const-leaf-pinned shape as `const/signed-token-codec.ts`.
 *
 * Replaces each credential SPAN with `CREDENTIAL_SPAN_SENTINEL` and leaves everything else byte-identical, so a
 * scrubbed command still names what failed. That is what separates it from `redactString`
 * (`utils/security/redact-string.ts`), which replaces the WHOLE value on any match -- right for a log line, useless
 * for a failure description whose entire purpose is identifying the failure.
 *
 * Total: never throws, and returns its input unchanged when nothing matches.
 */
export function scrubCredentialSpans(value: string): string {
	if (!isValidString(value)) return value;
	let scrubbed = value;
	for (const pattern of CREDENTIAL_CONTEXT_PATTERNS) {
		// Rest-args rather than named (match, prefix, suffix): a ONE-group pattern passes the numeric offset in the
		// third slot, so the suffix is read BY TYPE. Reading it positionally would splice an offset into the output.
		scrubbed = scrubbed.replace(withGlobalFlag(pattern), (...args: unknown[]): string => {
			const prefix = typeof args[1] === 'string' ? args[1] : '';
			const suffix = typeof args[2] === 'string' ? args[2] : '';
			return `${prefix}${CREDENTIAL_SPAN_SENTINEL}${suffix}`;
		});
	}
	for (const pattern of SENSITIVE_VALUE_PATTERNS) {
		scrubbed = scrubbed.replace(withGlobalFlag(pattern), CREDENTIAL_SPAN_SENTINEL);
	}
	return scrubbed;
}

/**
 * Scrub email SPANS out of a published string -- the PERSON-identifying half of the value screen, beside the
 * credential half above.
 *
 * HOMED HERE, not at the projection that first wrote it. It was authored module-private inside
 * `const/telemetry-redaction.ts`, which is ONE publication projection (the flow run/step surface), so a sibling
 * surface could not adopt it without importing a consumer leaf -- and the ai-telemetry ingest door duly did not,
 * publishing addresses verbatim into the retained Iceberg table for as long as it has existed (tracker A23, convicted
 * by CONTROL 4 of `tools/observability/__tests__/otel-seam-privacy-cardinality.test.ts`). A value screen every
 * redaction surface owes belongs in the vocabulary SSOT with `scrubCredentialSpans`, which is the leaf those surfaces
 * already import; the alternative -- exporting it from the projection -- would have made one consumer the SSOT for
 * another consumer's screen and left the next surface with the same adopt-or-fork choice. Moved rather than copied:
 * one spelling of the pass, one regex, and `EMAIL_EXTRACTION_REGEX` stays the shape SSOT.
 *
 * WHY THE PASS EXISTS AT ALL, carried from its original home. Keeping a person's identity out of the corpus was a
 * DELIBERATE property of the control that preceded the publish-by-default inversion, not a side effect of
 * deny-by-default -- that control's permit list admitted `userId` with the stated reason that an opaque internal id
 * "is not a name, email or phone". The directive that inverted the projection named tokens, keys and passwords;
 * reading it as also licensing user emails into a table the dashboard queries would reverse a privacy decision nobody
 * revisited, and the reversal would be silent and irreversible (the rows are already written). A
 * `REDACTION_PLACEHOLDERS.email` span still tells a reader the field was present and populated, which is nearly all of
 * its diagnostic value.
 *
 * SPAN-preserving like the credential scrub, so a message with an address embedded in it stays readable. Global-flagged
 * SSOT member used only via `.replace`, which resets `lastIndex` itself -- never `.test`, which would carry state
 * between calls.
 */
function scrubEmailSpans(value: string): string {
	// NECESSARY-CONDITION pre-filter, and a ReDoS cure rather than a micro-optimization. The local part
	// `[a-zA-Z0-9._%+-]+` is greedy and `@` is not in its class, so on a long run of local-part characters with no `@`
	// the engine consumes to end-of-string and backtracks ONCE PER START POSITION -- measured O(n^2) on this tree
	// (2026-08-06): 5.66x per doubling, and 26.7s for a single 256,001-character value. An address REQUIRES `@`, so a
	// value without one cannot match and skipping the replace is the IDENTITY -- no match moves and no address escapes.
	// The guard is what makes this safe where a rewrite is not: a `(?<![a-zA-Z0-9._%+-])` start-anchor looks equivalent
	// but DROPS a second address whose match resumes mid-run (`a@b.co-x@d.com` loses `-x@d.com`), silently publishing it.
	if (!value.includes('@')) return value;
	return value.replace(EMAIL_EXTRACTION_REGEX, REDACTION_PLACEHOLDERS.email);
}

/**
 * THE FULL VALUE SCREEN: credential spans, then email spans. The ONE spelling of the composition, and every surface
 * that publishes producer-controlled free text calls THIS rather than composing the two halves itself.
 *
 * WHY A THIRD EXPORT RATHER THAN TWO CALLS AT EACH SITE. The composition is a DECISION -- which screens a published
 * string owes, and in what order -- and it was spelled twice inside `const/telemetry-redaction.ts` while the
 * ai-telemetry ingest door spelled only half of it, which is the defect this function exists to make unrepeatable
 * (tracker A23). A surface that adopts one half and not the other now reads as an obvious omission at a single call
 * site instead of hiding as a plausible-looking one-pass call.
 *
 * ORDER IS LOAD-BEARING, credential first. The credential sentinel contains no `@`, so it cannot manufacture an
 * address; the reverse order would let a value's email placeholder sit inside a span the credential pass then
 * rewrites. Identical output on today's vocabulary, divergent the moment either side gains an arm -- so the order is
 * fixed HERE, once, rather than re-decided per consumer.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): value-imported by `const/telemetry-redaction.ts` and a
 *  `utils/observability` leaf; neither may reach a `utils/` home.
 */
export function scrubSensitiveSpans(value: string): string {
	return scrubEmailSpans(scrubCredentialSpans(value));
}

/**
 * Consolidated set of sensitive field names to redact.
 *
 * One consumer, a KEY-screen: the key screen of `const/telemetry-redaction.ts`, which withholds a matching key's whole
 * subtree before publishing a flow payload -- onto the run and step spans, into the local run journal's run and step
 * rows, or onto the tail log. Adding a name here therefore protects every surface that screen serves at once, which is
 * exactly what that module's header names as the cure for a residual rather than re-narrowing itself.
 *
 * THE CLIENT-IP FAMILY (added 2026-08-06 with the per-user sessions pipeline) sits alongside the regulated identifiers
 * below rather than with the credentials above, and for the same reason: an IP address identifies a PERSON, and the
 * sessions write path puts one on flow INPUT -- `user-session-record` takes `request_metadata` carrying `clientIp` off
 * the Worker's `collectRequestMetadata`, and the activity record carries `ip_address` -- so without these four names the
 * telemetry projection publishes a live user's address into a retained R2 table. All four
 * spellings are live in-tree (`clientIp` from the metadata collector, `ip_address` on the activity and the D1 column,
 * the camel/snake variants from payload builders), and the screen is exact-match plus lowercased, never substring.
 *
 * DELIBERATELY NOT MEMBERS: `geo_country` / `geo_region` / `country` / `region`. Country-and-region granularity does not
 * identify a person, it is the whole diagnostic point of the sessions geo columns, and the publish-by-default posture
 * withholds only what is secret. Narrower geo (`city`, `postalCode`, `latitude`, `longitude`) is a different question
 * and is NOT settled here -- nothing on this path writes those to a published column today.
 */
export const SENSITIVE_FIELDS: ReadonlySet<string> = new Set([
	'access_token',
	'accessToken',
	'refresh_token',
	'refreshToken',
	EXTRACT_STRING_INPUT.AUTH_TOKEN,
	'authToken',
	'token',
	'bearer',
	'api_key',
	'apiKey',
	'api_token',
	'apiToken',
	'secret',
	'secret_key',
	'secretKey',
	'password',
	'credentials',
	'authorization',
	'private_key',
	'privateKey',
	'credit_card',
	'creditCard',
	'ssn',
	'bank_account',
	'bankAccount',
	'ip_address',
	'ipAddress',
	'client_ip',
	// BOUND, not re-typed: this exact spelling is the collector's own key
	// (`REQUEST_METADATA_KEY.CLIENT_IP`, `const/request-metadata-sanitize.ts`), which is the leaf that decides what
	// `request_metadata` carries. A re-typed copy fails in the DANGEROUS direction -- rename the collector's key and
	// the screen keeps covering the retired spelling while the live one publishes -- and that is precisely the
	// publish-a-live-address failure this family's rationale above names. Bound, a rename reaches the screen with no
	// second edit, and the literal `SENSITIVE_FIELDS.has('clientIp')` case in this leaf's suite reds if the retired
	// spelling still needs covering, so the drop is a decision an author is made to take rather than one that happens
	// silently. The three siblings above stay literal: no const owns them, and inventing owners for spellings whose
	// producers are a D1 column and two payload builders would name decisions nobody makes.
	REQUEST_METADATA_KEY.CLIENT_IP,
]);
